import { useEffect, useRef, useState } from "react";
import { api, fmtNum, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X, UploadCloud, Loader2, GripVertical } from "lucide-react";

export default function EstructuraPresupuestal({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [form, setForm] = useState({ nombre_padre: "", nombre_hijo: "", cantidad_default: "" });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const fileRef = useRef();
  const dragIndex = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/presupuesto/estructura").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const onImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/presupuesto/estructura/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.importados} importados · ${r.data.omitidos_duplicados} duplicados omitidos`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/presupuesto/estructura", {
        nombre_padre: form.nombre_padre, nombre_hijo: form.nombre_hijo,
        cantidad_default: parseFloat(form.cantidad_default) || 0,
      });
      toast.success(form.nombre_hijo.trim() ? "Registro agregado" : "Padre agregado");
      setForm({ nombre_padre: "", nombre_hijo: "", cantidad_default: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/presupuesto/estructura/${id}`, {
        nombre_padre: editForm.nombre_padre, nombre_hijo: editForm.nombre_hijo,
        cantidad_default: parseFloat(editForm.cantidad_default) || 0,
      });
      toast.success("Actualizado");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este registro de la estructura?")) return;
    await api.delete(`/presupuesto/estructura/${id}`);
    toast.success("Eliminado");
    load();
  };

  const persistOrder = async (ordered) => {
    setRows(ordered);
    try {
      await api.put("/presupuesto/estructura/reorder", { ids: ordered.map((r) => r.id) });
    } catch (err) {
      toast.error("No se pudo guardar el orden");
      load();
    }
  };

  const onDrop = (targetIdx) => {
    const from = dragIndex.current;
    setDragOver(null);
    dragIndex.current = null;
    if (from === null || from === targetIdx) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(targetIdx, 0, moved);
    persistOrder(next);
  };

  const input = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900";

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex items-center gap-3 flex-wrap">
          <input ref={fileRef} data-testid="estructura-file" type="file" accept=".xlsx" hidden onChange={(e) => onImport(e.target.files?.[0])} />
          <button data-testid="estructura-import-btn" onClick={() => fileRef.current?.click()} disabled={importing}
            className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 flex items-center gap-2 disabled:opacity-50">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Importar Excel (.xlsx)
          </button>
          <span className="text-xs text-slate-500">Columnas: Padre · Hijo · Cantidad. Arrastra <GripVertical className="inline h-3 w-3" /> para reordenar.</span>
        </div>
      )}

      {canEdit && (
        <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end bg-slate-50 border border-slate-200 rounded-md p-3">
          <div>
            <label className="text-xs text-slate-500">Padre</label>
            <input data-testid="estructura-padre" className={input} value={form.nombre_padre} onChange={(e) => setForm({ ...form, nombre_padre: e.target.value })} required />
          </div>
          <div>
            <label className="text-xs text-slate-500">Hijo (opcional)</label>
            <input data-testid="estructura-hijo" className={input} value={form.nombre_hijo} onChange={(e) => setForm({ ...form, nombre_hijo: e.target.value })} placeholder="Dejar vacío = solo Padre" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Cantidad default</label>
            <input data-testid="estructura-cantidad" type="number" step="any" className={input} value={form.cantidad_default} onChange={(e) => setForm({ ...form, cantidad_default: e.target.value })} placeholder="0" />
          </div>
          <button data-testid="estructura-add-btn" className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 flex items-center justify-center gap-2"><Plus className="h-4 w-4" /> Agregar</button>
        </form>
      )}

      <div className="border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
            <tr>
              {canEdit && <th className="w-8" />}
              <th className="px-4 py-2.5 font-medium text-left">Padre</th>
              <th className="px-4 py-2.5 font-medium text-left">Hijo</th>
              <th className="px-4 py-2.5 font-medium text-right">Cantidad default</th>
              {canEdit && <th className="px-4 py-2.5 w-24" />}
            </tr>
          </thead>
          <tbody data-testid="estructura-table">
            {loading && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Cargando…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Sin estructura. Importa un Excel o agrega registros.</td></tr>}
            {!loading && rows.map((r, idx) => (
              <tr key={r.id}
                data-testid={`estructura-row-${r.id}`}
                onDragOver={(e) => { if (canEdit && editing !== r.id) { e.preventDefault(); setDragOver(idx); } }}
                onDrop={() => onDrop(idx)}
                className={`border-b border-slate-100 hover:bg-slate-50/50 ${dragOver === idx ? "bg-slate-100" : ""}`}>
                {editing === r.id ? (
                  <>
                    {canEdit && <td />}
                    <td className="px-4 py-2"><input className={input} value={editForm.nombre_padre} onChange={(e) => setEditForm({ ...editForm, nombre_padre: e.target.value })} /></td>
                    <td className="px-4 py-2"><input className={input} value={editForm.nombre_hijo} onChange={(e) => setEditForm({ ...editForm, nombre_hijo: e.target.value })} placeholder="(opcional)" /></td>
                    <td className="px-4 py-2"><input type="number" step="any" className={`${input} text-right`} value={editForm.cantidad_default} onChange={(e) => setEditForm({ ...editForm, cantidad_default: e.target.value })} /></td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button onClick={() => saveEdit(r.id)} className="text-emerald-600 mr-2" data-testid={`estructura-save-${r.id}`}><Check className="h-4 w-4" /></button>
                      <button onClick={() => setEditing(null)} className="text-slate-400"><X className="h-4 w-4" /></button>
                    </td>
                  </>
                ) : (
                  <>
                    {canEdit && (
                      <td className="pl-2 text-slate-300"
                        draggable
                        onDragStart={() => { dragIndex.current = idx; }}
                        onDragEnd={() => { setDragOver(null); dragIndex.current = null; }}>
                        <GripVertical className="h-4 w-4 cursor-grab" data-testid={`estructura-drag-${r.id}`} />
                      </td>
                    )}
                    <td className="px-4 py-2.5 font-medium text-slate-900">{r.nombre_padre}</td>
                    <td className="px-4 py-2.5 text-slate-700">{r.nombre_hijo ? r.nombre_hijo : <span className="text-xs italic text-slate-400">— solo Padre —</span>}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtNum(r.cantidad_default)}</td>
                    {canEdit && (
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <button data-testid={`estructura-edit-${r.id}`} onClick={() => { setEditing(r.id); setEditForm({ nombre_padre: r.nombre_padre, nombre_hijo: r.nombre_hijo, cantidad_default: r.cantidad_default }); }} className="text-slate-400 hover:text-slate-900 mr-3"><Pencil className="h-4 w-4" /></button>
                        <button data-testid={`estructura-del-${r.id}`} onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </td>
                    )}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
